import { GarageAccount, MonthlyAccount, MonthlyCar, MonthlyPermit, MonthlyPermitStatusEnum, MonthlyPermitPermitClassificationEnum, Theme, Vehicle, VehicleInfoOversizeTypeEnum } from '@parkingbase/ecommerce-api-dev';

import { isBefore, isAfter, parseISO, startOfDay } from 'date-fns';
import { getDaysBetweenDates } from './date-time-utils';
import { getContactName } from './contact-utils';

/**
 * Returns the effective total balance for a monthly account.
 *
 * - If the account is linked to a primary root, use the statement balance.
 * - Otherwise, fall back to the regular balance due.
 */
export const getMonthlyAccountTotalBalance = (garageAccount: GarageAccount): number => {
  const { monthlyAccount } = garageAccount;

  return monthlyAccount.accountLinkage?.primaryRoot
    ? monthlyAccount.statementBalance || 0
    : monthlyAccount.balanceDue || 0;
};

/**
 * Returns a copy of `garageAccounts` with the computed `statementBalance` merged onto
 * each Primary Billing Account (PBA) from the `/user/subaccounts` map.
 *
 * The `/user/domain/garageAccounts` endpoint does not compute `statementBalance` for a
 * PBA — only `/user/subaccounts` does. Without this merge a PBA reads `statementBalance = 0`
 * and shows "NO DUES" / "To Pay $0.00" even when it owes. The legacy reservation site does
 * the same merge in MonthlyInvoices.new.tsx. (PB-3542)
 *
 * ## Invariants:
 * - Only PBAs (`accountLinkage.primaryRoot`) are enriched; others are returned untouched.
 * - When the map has no `statementBalance` for a PBA, the account is left as-is (never zeroed).
 * - New objects are returned for enriched accounts; the input array/items are not mutated.
 */
export const enrichStatementBalances = (
  garageAccounts: GarageAccount[],
  subAccountsMap: Record<string, MonthlyAccount> | null | undefined
): GarageAccount[] => {
  if (!subAccountsMap) return garageAccounts;

  return garageAccounts.map((garageAccount) => {
    const { monthlyAccount } = garageAccount;
    if (!monthlyAccount?.accountLinkage?.primaryRoot) return garageAccount;

    const documentId = monthlyAccount.documentId;
    const statementBalance = documentId ? subAccountsMap[documentId]?.statementBalance : undefined;
    if (statementBalance == null) return garageAccount;

    return { ...garageAccount, monthlyAccount: { ...monthlyAccount, statementBalance } };
  });
};

/**
 * Determines whether payments should be blocked for a garage account
 * due to past-due balances.
 *
 * Rules:
 * - If there is a non-trivial outstanding balance (>= $0.01) and an
 *   `outstandingBalanceAging` date, compute how many days it has been
 *   outstanding.
 * - If the theme specifies `daysPastDueBlockPayments`, block payments
 *   when the outstanding balance age is greater than that threshold.
 * - If the theme does not specify a threshold, payments are not blocked.
 */
export const pastDuePaymentBlocked = (garageAccount: GarageAccount, theme: Theme): boolean => {
  const { monthlyAccount } = garageAccount;

  const totalBalance = getMonthlyAccountTotalBalance(garageAccount);

  // Only consider aging if there is a meaningful outstanding balance.
  const hasOutstandingBalance = Math.abs(monthlyAccount.outstandingBalance || 0) >= 0.01;

  const agingInDays =
    hasOutstandingBalance && monthlyAccount.outstandingBalanceAging
      ? getDaysBetweenDates(monthlyAccount.outstandingBalanceAging, new Date().toISOString())
      : 0;

  const blockAfterDays = theme.daysPastDueBlockPayments;

  // If no threshold is configured, do not block payments.
  if (!blockAfterDays) {
    return false;
  }

  // Block payments only when there is a meaningful balance and it is older
  // than the configured threshold.
  return totalBalance > 0.01 && agingInDays > blockAfterDays;
};

/**
 * Whether the operator has turned off one-off online payments for this account's garage.
 *
 * The garage policy overrides the company setting in BOTH directions — ENABLE keeps
 * payments on at a garage whose company has them off. Only FOLLOW_COMPANY defers to the
 * company toggle. Port of legacy `isOneTimePaymentsDisabled` (constants.ts:1374).
 *
 * Deliberately not folded into `getGarageAccountTotalDue`: zeroing the total there would
 * relabel the account as NO DUES while it still owes money.
 */
export const oneTimePaymentsDisabled = (garageAccount: GarageAccount, theme: Theme): boolean => {
  const policy = garageAccount.garage?.oneTimePaymentPolicy;

  if (policy === 'DISABLE') {
    return true;
  }
  if (policy === 'ENABLE') {
    return false;
  }
  return !!theme.disableOneTimePayments;
};

/**
 * Calculates the total amount due for a garage account that can be paid online.
 *
 * Returns 0 when:
 * - Payments are blocked due to past-due rules.
 * - Online payments are explicitly blocked for the monthly account.
 * - The garage does not support monthly reservations.
 */
export const getGarageAccountTotalDue = (garageAccount: GarageAccount, theme: Theme): number => {
  const { monthlyAccount, garage } = garageAccount;

  const paymentsBlocked =
    pastDuePaymentBlocked(garageAccount, theme) ||
    monthlyAccount.blockOnlinePayments ||
    !garage.monthlyReservationEnabled;

  if (paymentsBlocked) {
    return 0;
  }

  return getMonthlyAccountTotalBalance(garageAccount);
};

const isActiveAccount = (account: GarageAccount): boolean =>
  account.monthlyAccount.status === 'ACTIVE';

const isActivePermit = (permit: MonthlyPermit): boolean =>
  permit.status === MonthlyPermitStatusEnum.ACTIVE && !!permit.permitNumber;

/** Uppercase-alnum plate, so "abc-123" and "ABC 123" compare equal. */
const normalizePlate = (plate: string | undefined): string =>
  (plate ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');

const normalizeState = (state: string | undefined): string => (state ?? '').trim().toUpperCase();

/**
 * Whether two plate/state pairs name the same car.
 *
 * ## Invariants:
 * - A blank state on either side matches any state. Why: the profile vehicle
 *   created at POS signup carries no state while its permit car does
 *   (PB-3269), so an exact state compare showed the same car twice.
 * - Two different non-blank states stay different cars.
 */
const isSamePlateAndState = (
  plateA: string | undefined,
  stateA: string | undefined,
  plateB: string | undefined,
  stateB: string | undefined
): boolean => {
  const plate = normalizePlate(plateA);
  if (!plate || plate !== normalizePlate(plateB)) return false;
  const a = normalizeState(stateA);
  const b = normalizeState(stateB);
  return !a || !b || a === b;
};

const isCarMatchingVehicle = (
  car: { plate?: string; state?: string },
  plate: string | undefined,
  state: string | undefined
): boolean => isSamePlateAndState(car.plate, car.state, plate, state);

const isCarWithinDateRange = (
  car: { startDate?: string; endDate?: string },
  now: Date
): boolean => {
  if (!car.startDate || !car.endDate) return false;
  return parseISO(car.startDate) <= now && parseISO(car.endDate) >= now;
};

/**
 * Returns deduplicated active permits linked to a vehicle by matching
 * plate, state, and date range across all active garage accounts.
 */
export const getLinkedPermits = (
  vehicle: Vehicle,
  garageAccounts: GarageAccount[]
): MonthlyPermit[] => {
  const now = new Date();
  const { vehicleLicensePlate, vehicleState } = vehicle.vehicleInfo;

  const permits = (garageAccounts ?? [])
    .filter(isActiveAccount)
    .flatMap((account) => account.monthlyAccount.monthlyPermits ?? [])
    .filter(
      (permit) =>
        isActivePermit(permit) &&
        permit.monthlyCars?.some(
          (car) =>
            isCarWithinDateRange(car, now) &&
            isCarMatchingVehicle(car, vehicleLicensePlate, vehicleState)
        )
    );

  return [...new Set(permits)];
};

/**
 * A Vehicle plus a flag marking whether it originated from a monthly permit
 * (POS / Back Office / Quote) rather than the editable profile vehicle store.
 * Permit-sourced entries are read-only in My Account > Vehicles and carry the
 * permit number(s) they came from. Extends the API `Vehicle` (never casts it).
 */
export interface CombinedVehicle extends Vehicle {
  /** true when this entry was aggregated from a monthly permit's MonthlyCar. */
  permitSourced: boolean;
  /** Permit number(s) this vehicle is associated with (permit-sourced only). */
  permitNumbers: string[];
}

/** Normalized dedupe key for a vehicle: uppercase-alnum plate + uppercase state. */
const plateStateKey = (plate: string | undefined, state: string | undefined): string =>
  `${normalizePlate(plate)}|${(state ?? '').toUpperCase()}`;

// Known oversize-type values — a MonthlyCar's free-text `type` is forwarded only
// when it maps to one, otherwise it falls back to STANDARD.
const OVERSIZE_TYPE_VALUES = new Set<string>(Object.values(VehicleInfoOversizeTypeEnum));

const toOversizeType = (type: string | undefined): VehicleInfoOversizeTypeEnum => {
  const normalized = (type ?? '').toUpperCase();
  return OVERSIZE_TYPE_VALUES.has(normalized)
    ? (normalized as VehicleInfoOversizeTypeEnum)
    : VehicleInfoOversizeTypeEnum.STANDARD;
};

/**
 * Maps a permit's MonthlyCar into a read-only CombinedVehicle carrying full
 * vehicleInfo, so it is both displayable and bookable without being persisted
 * as a profile Vehicle.
 */
const monthlyCarToVehicle = (car: MonthlyCar, permit: MonthlyPermit): CombinedVehicle => {
  const make = car.make ?? '';
  const model = car.model ?? '';
  const name = [make, model].filter(Boolean).join(' ');
  // MonthlyCar defaults height/length to -1; only forward real measurements.
  const height = typeof car.height === 'number' && car.height > 0 ? car.height : undefined;
  const length = typeof car.length === 'number' && car.length > 0 ? car.length : undefined;

  return {
    documentId: `permit-car::${permit.documentId || permit.permitNumber}::${plateStateKey(car.plate, car.state)}`,
    defaultVehicle: false,
    permitSourced: true,
    permitNumbers: permit.permitNumber ? [permit.permitNumber] : [],
    vehicleInfo: {
      vehicleMake: make,
      vehicleModel: model,
      vehicleState: (car.state ?? '').toUpperCase(),
      vehicleColor: car.color ?? '',
      vehicleLicensePlate: car.plate ?? '',
      vehicleName: name,
      vehicleYear: car.year,
      vehicleVin: car.vin,
      vehicleBody: car.body,
      height,
      length,
      oversizeType: toOversizeType(car.type),
    },
  };
};

/**
 * Aggregates the customer's POS/Back-Office/Quote-created vehicles (MonthlyCar
 * entries on active permits of active monthly accounts) into read-only
 * CombinedVehicles, deduplicated by plate+state against profile vehicles.
 *
 * ## Invariants:
 * - Active only: only active accounts, active permits (`isActivePermit`), and
 *   cars whose validity window covers now (`isCarWithinDateRange`) are included.
 *   Why: mirrors the legacy site's getValidMonthlyCars "active car" semantics —
 *   surfacing expired cars would clutter the Vehicles list and booking dropdown.
 * - Profile wins on conflict: a permit car whose plate+state matches an existing
 *   profile Vehicle is dropped, so the editable record is the one shown. A blank
 *   state on either side counts as a match (`isSamePlateAndState`, PB-3269).
 * - Cars sharing plate+state across permits collapse to one entry whose
 *   permitNumbers lists every source permit. Same blank-state rule: a car with
 *   no state joins the entry for the same plate. The first car seen is kept.
 * - Cars with no plate are skipped (no identity to dedupe or book against).
 */
export const getAggregatedPermitVehicles = (
  garageAccounts: GarageAccount[] | undefined,
  profileVehicles: Vehicle[] | undefined
): CombinedVehicle[] => {
  const now = new Date();
  const profileInfos = (profileVehicles ?? []).map((v) => v.vehicleInfo);
  const matchesProfile = (car: MonthlyCar): boolean =>
    profileInfos.some((info) =>
      isSamePlateAndState(car.plate, car.state, info?.vehicleLicensePlate, info?.vehicleState)
    );

  const byKey = new Map<string, CombinedVehicle>();

  (garageAccounts ?? [])
    .filter(isActiveAccount)
    .flatMap((account) => account.monthlyAccount.monthlyPermits ?? [])
    .filter(isActivePermit)
    .forEach((permit) => {
      (permit.monthlyCars ?? [])
        .filter((car) => !!car.plate && isCarWithinDateRange(car, now))
        .forEach((car) => {
          const key = plateStateKey(car.plate, car.state);
          if (matchesProfile(car)) return; // profile vehicle wins
          const existing =
            byKey.get(key) ??
            [...byKey.values()].find((v) =>
              isSamePlateAndState(
                car.plate,
                car.state,
                v.vehicleInfo.vehicleLicensePlate,
                v.vehicleInfo.vehicleState
              )
            );
          if (existing) {
            if (permit.permitNumber && !existing.permitNumbers.includes(permit.permitNumber)) {
              existing.permitNumbers.push(permit.permitNumber);
            }
            return;
          }
          byKey.set(key, monthlyCarToVehicle(car, permit));
        });
    });

  return [...byKey.values()];
};

/**
 * Enriched permit data for display in vehicle details view.
 */
export interface LinkedPermitWithDetails {
  permit: MonthlyPermit;
  garageName: string;
  accountNumber: string;
  contactName: string;
  spaceNumber: string;
}

/**
 * Returns active permits linked to a vehicle, enriched with
 * garage name, account number, contact name, and space number for display.
 */
export const getLinkedPermitsWithDetails = (
  vehicle: Vehicle,
  garageAccounts: GarageAccount[]
): LinkedPermitWithDetails[] => {
  const now = new Date();
  const { vehicleLicensePlate, vehicleState } = vehicle.vehicleInfo;
  const results: LinkedPermitWithDetails[] = [];

  (garageAccounts ?? []).filter(isActiveAccount).forEach((account) => {
    const { monthlyAccount, garage } = account;

    (monthlyAccount.monthlyPermits ?? []).forEach((permit) => {
      if (
        isActivePermit(permit) &&
        permit.monthlyCars?.some(
          (car) =>
            isCarWithinDateRange(car, now) &&
            isCarMatchingVehicle(car, vehicleLicensePlate, vehicleState)
        )
      ) {
        const contactName = getContactName(monthlyAccount.contact);

        const spaceNumbers = permit.spaceNumbers ?? [];

        results.push({
          permit,
          garageName: garage.websiteName || garage.name || '',
          accountNumber: monthlyAccount.accountNumber || '',
          contactName,
          spaceNumber: spaceNumbers.join(', '),
        });
      }
    });
  });

  return results;
};

/**
 * Formats permit numbers from monthly permits into a display string.
 * Prepends '#' if missing. e.g. "#0004, #0026"
 */
export const getPermitNumbers = (
  monthlyPermits: { permitNumber?: string }[] | undefined
): string => {
  if (!monthlyPermits?.length) return '';
  return monthlyPermits
    .map((p) => p.permitNumber)
    .filter(Boolean)
    .map((num) => (num!.startsWith('#') ? num : `#${num}`))
    .join(', ');
};

// ---------------------------------------------------------------------------
// Guest Pass eligibility helpers
// ---------------------------------------------------------------------------

/**
 * Checks whether a MonthlyPermit is currently active based on its status,
 * start/end dates, and suspension windows.
 */
const isPermitActiveForDate = (date: Date, permit: MonthlyPermit): boolean => {
  const status = permit.status;

  if (
    status === MonthlyPermitStatusEnum.INACTIVE ||
    status === MonthlyPermitStatusEnum.PENDING ||
    status === MonthlyPermitStatusEnum.QUOTE ||
    status === MonthlyPermitStatusEnum.WAITLIST
  ) {
    return false;
  }

  if (status === MonthlyPermitStatusEnum.SUSPENDED) {
    const suspStart = permit.suspensionStartDate ? parseISO(permit.suspensionStartDate) : null;
    const suspEnd = permit.suspensionEndDate ? parseISO(permit.suspensionEndDate) : null;
    return (
      (suspStart != null && isBefore(date, suspStart)) ||
      (suspEnd != null && isAfter(date, suspEnd))
    );
  }

  if (status === MonthlyPermitStatusEnum.TEMPORARY) {
    const suspStart = permit.suspensionStartDate ? parseISO(permit.suspensionStartDate) : null;
    const suspEnd = permit.suspensionEndDate ? parseISO(permit.suspensionEndDate) : null;
    if (!suspStart || !suspEnd) return false;
    return !isBefore(date, suspStart) && !isAfter(date, suspEnd);
  }

  // ACTIVE status — check date range
  const start = permit.startDate ? parseISO(permit.startDate) : null;
  const end = permit.endDate ? parseISO(permit.endDate) : null;
  if (start && end) {
    const diffMs = Math.abs(end.getTime() - start.getTime());
    // Very short-range permits (< 2 days) are considered always active
    if (diffMs < 172800000) return true;
    if (isAfter(start, date)) return false;
    return !isBefore(end, date);
  }

  return true;
};

/**
 * Checks whether a MonthlyAccount is active for a given date
 * based on its status and suspension windows.
 */
const isMonthlyAccountActiveForDate = (account: MonthlyAccount, date: Date): boolean => {
  const status = account.status;

  if (status === 'SUSPENDED') {
    const suspStart = account.suspensionStartDate ? parseISO(account.suspensionStartDate) : null;
    const suspEnd = account.suspensionEndDate ? parseISO(account.suspensionEndDate) : null;
    return (
      (suspStart != null && !isAfter(date, suspStart)) ||
      (suspEnd != null && !isBefore(date, suspEnd))
    );
  }
  if (status === 'TEMPORARY') {
    const suspStart = account.suspensionStartDate ? parseISO(account.suspensionStartDate) : null;
    const suspEnd = account.suspensionEndDate ? parseISO(account.suspensionEndDate) : null;
    if (!suspStart || !suspEnd) return false;
    return !isBefore(date, suspStart) && !isAfter(date, suspEnd);
  }
  if (status === 'INACTIVE' || status === 'PENDING' || status === 'QUOTE') {
    return false;
  }

  return true; // ACTIVE
};

/**
 * Checks whether at least one permit on the account is currently active.
 */
const hasActivePermits = (permits: MonthlyPermit[]): boolean => {
  const today = startOfDay(new Date());
  return permits.some((p) => isPermitActiveForDate(today, p));
};

/**
 * Checks whether a garage account matches the garage's guest pass config
 * (account types + permit classifications).
 */
const matchesGuestPassConfig = (account: GarageAccount): boolean => {
  const configs = account.garage?.guestPassSetting?.guestPassConfigs ?? [];
  const monthly = account.monthlyAccount;
  const permits = monthly?.monthlyPermits ?? [];

  const accountTypes: string[] = [];
  const permitClassifications: string[] = [];

  for (const config of configs) {
    if (config.accountTypes) accountTypes.push(...config.accountTypes);
    if (config.permitClassifications) permitClassifications.push(...config.permitClassifications);
  }

  // Check account type match
  const hasMatchingAccountType = accountTypes.includes(monthly?.accountType as string);

  // If account has no permits, only check account type
  if (permits.length === 0) {
    return hasMatchingAccountType;
  }

  // If account has permits, need both account type match + active permits
  const accountTypeMatch = hasMatchingAccountType && hasActivePermits(permits);

  // Also check permit classification match
  const today = startOfDay(new Date());
  const hasMatchingPermitClassification = permits.some(
    (permit) =>
      (permitClassifications.includes(permit.permitClassification as string) ||
        permit.permitClassification === MonthlyPermitPermitClassificationEnum.PASS) &&
      (isPermitActiveForDate(today, permit) ||
        permit.status === MonthlyPermitStatusEnum.SUSPENDED ||
        permit.status === MonthlyPermitStatusEnum.TEMPORARY)
  );

  return accountTypeMatch || hasMatchingPermitClassification;
};

/**
 * Returns garage accounts that are eligible to create guest passes.
 *
 * Eligibility requires:
 * - Theme has enableGuestPass
 * - Garage has enableGuestPass + guestPassSetting.enableParkingPassAccounts
 * - Garage allows pass creation (allowParkingPassCreation)
 * - Account's guestPassSetting is not disabled (parkingPassDisabled)
 * - Account matches garage's guest pass config (account types / permit classifications)
 */
export const getGuestPassEligibleAccounts = (
  garageAccounts: GarageAccount[],
  theme: Theme | null
): GarageAccount[] => {
  if (!theme?.enableGuestPass) return [];

  return garageAccounts.filter((a) => {
    const garage = a.garage;
    const garageSetting = garage?.guestPassSetting;

    // Garage-level checks
    if (!garage?.enableGuestPass || !garageSetting?.enableParkingPassAccounts) return false;
    if (!garageSetting.allowParkingPassCreation) return false;

    // Account-level checks
    const accountSetting = a.monthlyAccount?.guestPassSetting;
    if (accountSetting?.parkingPassDisabled) return false;

    // Account type / permit classification matching
    return matchesGuestPassConfig(a);
  });
};

/**
 * Returns garage accounts that are eligible to purchase guest pass credits.
 *
 * Same as creation eligibility but excludes accounts with unlimited passes
 * (they don't need to buy credits) and does NOT require allowParkingPassCreation.
 */
export const getGuestPassPurchaseEligibleAccounts = (
  garageAccounts: GarageAccount[],
  theme: Theme | null
): GarageAccount[] => {
  if (!theme?.enableGuestPass) return [];

  return garageAccounts.filter((a) => {
    const garage = a.garage;
    const garageSetting = garage?.guestPassSetting;

    if (!garage?.enableGuestPass || !garageSetting?.enableParkingPassAccounts) return false;

    const accountSetting = a.monthlyAccount?.guestPassSetting;
    if (accountSetting?.parkingPassDisabled) return false;
    if (accountSetting?.allowUnlimitedPasses) return false;

    return matchesGuestPassConfig(a);
  });
};

/**
 * Returns the total remaining pass count across eligible accounts.
 * Returns 'UNLIMITED' if any account has unlimited passes.
 */
export const getTotalRemainingPasses = (accounts: GarageAccount[]): number | string => {
  let total = 0;
  for (const a of accounts) {
    const setting = a.monthlyAccount?.guestPassSetting;
    if (!setting) continue;
    if (setting.allowUnlimitedPasses) return 'UNLIMITED';
    total += (Number(setting.remainingGuestPasses) || 0) + (Number(setting.purchasedPass) || 0);
  }
  return total;
};

/**
 * Checks whether a specific date is valid for guest pass creation
 * based on the account's permit validity and account status.
 */
export const isDateValidForGuestPass = (date: Date, account: GarageAccount): boolean => {
  const permits = account.monthlyAccount?.monthlyPermits ?? [];

  // Check account status
  if (!isMonthlyAccountActiveForDate(account.monthlyAccount, date)) {
    return false;
  }

  // If no permits, just check account status (already passed above)
  if (permits.length === 0) return true;

  // At least one permit must be active for the given date
  return permits.some((permit) => isPermitActiveForDate(date, permit));
};
